import React, { useCallback } from "react";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import { DeleteIcon } from "../Icons";
import { STATIC_ICONS } from "../Constants";
import CloudUpload from '@mui/icons-material/CloudUploadOutlined';

import { useState, useEffect, useRef, useContext } from 'react';

import commonTranslations from '../languages/common.json';
import { LanguageContext } from '../context/LanguageContext';

const ImageUploadInput = ({ onChange, image }) => {
  const { lang } = useContext(LanguageContext);
  const t = commonTranslations[lang].common.ImageButtonSettings;
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      onChange({ file, imageUrl: null });
    }
  };

  const handleDelete = () => {
    setPreviewUrl(null);
    fileInputRef.current.value = null;
    onChange({ file: null, imageUrl: "" });
  };

  useEffect(() => {
    if (image?.file instanceof File) {
      const objectUrl = URL.createObjectURL(image.file);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (typeof image?.imageUrl === 'string') {
      setPreviewUrl(STATIC_ICONS + image.imageUrl);
    }
  }, [image]);

  return (
    <div className='edit-content-part' style={{ flex: 0.65 }}>
      <div className='edit-form-item' style={{ alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <input
          ref={fileInputRef}
          type='file'
          accept='image/*'
          title=''
          onChange={handleFileChange}
          className='edit-logo-section'
        />

        {previewUrl ? (
          <>
            <img src={previewUrl} alt='Uploaded' onClick={handleDelete} style={{ borderRadius: '4px', cursor: 'pointer' }} />
            <DeleteIcon onClick={handleDelete} style={{ cursor: 'pointer' }} />
          </>
        ) : (
          <>
            <CloudUpload
              style={{
                position: 'absolute',
                top: '15%',
                pointerEvents: 'none',
                color: 'var(--accent)',
                transform: 'scale(2.5)',
              }}
            />
            <div style={{ position: 'absolute', pointerEvents: 'none', fontWeight: 500 }}>{t.uploadText}</div>
            <div
              style={{ position: 'absolute', left: '5%', bottom: '15%', pointerEvents: 'none' }}
              className='edit-helper'
            >
              {t.uploadHelper}
            </div>
          </>
        )}
      </div>
    </div>
  );
};


const DraggableImageItem = ({ image, index, moveItem, edit, setEdit }) => {
  const { lang } = useContext(LanguageContext);
  const t = commonTranslations[lang].common.ImageButtonSettings;
  const [, ref] = useDrag({
    type: "OPTION",
    item: { index },
  });

  const [, drop] = useDrop({
    accept: "OPTION",
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  const updateImage = (key, value) => {

    const updatedImages = [...edit.props.images];
    updatedImages[index][key] = value;
    setEdit({ ...edit, props: { ...edit.props, images: updatedImages } });
  };

  const handleDelete = () => {
    const updatedImages = edit.props.images.filter((_, i) => i !== index);
    setEdit({ ...edit, props: { ...edit.props, images: updatedImages } });
  };

  return (
    <div
      ref={(node) => ref(drop(node))}
      className="form-item"
      key={index}
      style={{
        display: "flex",
        marginTop: "40px",
        gap: "10px",
        alignItems: "center",
        cursor: "grab",
      }}
    >
      <div
        style={{
          display: "flex",
          width: "100%",
          gap: "10px",
          alignItems: "center",
        }}
      >
        <input
          type="number"
          className="add-column"
          placeholder={t.valuePlaceholder}
          id={"value-" + index}
          style={{
            flex: "0.3",
            padding: "4px 10px",
            color: "var(--cell--color)",
            caretColor: "var(--cell--color)",
          }}
          value={image.value}
          onChange={(e) => updateImage("value", e.target.value)}
        />
        <ImageUploadInput
          image={image}
          onChange={({ file, imageUrl }) => {
            updateImage("file", file);
            updateImage("imageUrl", imageUrl);
          }}
        />
        <button className="widget-btn">
          <OpenWithIcon />
        </button>
        <button
          onClick={handleDelete}
          className="widget-btn widget-btn-remove"
          style={{ padding: "5px 10px" }}
        >
          <DeleteIcon color={"var(--root-background)"} width="16px" height="16px" />
        </button>
      </div>
    </div>
  );
};

const ImageButtonSettings = ({ edit, setEdit }) => {
  const moveItem = useCallback(
    (fromIndex, toIndex) => {
      const updatedImages = [...edit.props.images];
      const [movedItem] = updatedImages.splice(fromIndex, 1);
      updatedImages.splice(toIndex, 0, movedItem);
      setEdit({ ...edit, props: { ...edit.props, images: updatedImages } });
    },
    [edit, setEdit]
  );

  return (
    <DndProvider backend={HTML5Backend}>
      {edit.props.images?.map((image, index) => (
        <DraggableImageItem
          key={index}
          index={index}
          image={image}
          moveItem={moveItem}
          edit={edit}
          setEdit={setEdit}
        />
      ))}
    </DndProvider>
  );
};

export default ImageButtonSettings;
